import { useState, useRef } from "react";
import { supabase } from "./supabase";
import { likeExact, seedDefaultTemplates } from "./accountSetup";
import { COUNTRIES, CURRENCIES, regionsFor, presetFor } from "./taxConfig"; // PATCH oct18-tax

// PATCH oct21-ui-login: teal look like the app, logo teal with the orange PRO band, dark mode
const DARK = (function () { try { return localStorage.getItem("darkMode") === "true"; } catch (e) { return false; } })();
const C = {
  teal: "#2F6F6B",
  accent: DARK ? "#7CC2BB" : "#2F6F6B",
  tint: DARK ? "#1C2E2C" : "#E6F0EF",
  orange: "#C9620A",
  page: DARK ? "#121615" : "#E2E7E6", // PATCH oct21-ui-fix2: light page
  card: DARK ? "#1A201F" : "#FFFFFF",
  section: DARK ? "#161B1A" : "#F7F9F8",
  text: DARK ? "#E8EEED" : "#1C2B2A",
  sub: DARK ? "#B8C2C1" : "#3A4645",
  muted: DARK ? "#8E9998" : "#5F6B6A",
  border: DARK ? "#2C3433" : "#E2E7E6",
  inputBg: DARK ? "#1F2625" : "#FFFFFF",
  inputBorder: DARK ? "#3A4443" : "#CBD3D2",
  error: DARK ? "#F0776B" : "#B42318",
  errorBg: DARK ? "#3A1F1D" : "#FBEAE8",
  success: DARK ? "#5DB683" : "#2F7D4F",
  successBg: DARK ? "#16301F" : "#E5F4EA",
  warning: DARK ? "#E9A23B" : "#B45309",
  warningBg: DARK ? "#3A2A12" : "#FDF3E3",
};

function validatePassword(pw) {
  const rules = [
    { label: "At least 8 characters", ok: pw.length >= 8 },
    { label: "Uppercase letter (A-Z)", ok: /[A-Z]/.test(pw) },
    { label: "Lowercase letter (a-z)", ok: /[a-z]/.test(pw) },
    { label: "Number (0-9)", ok: /[0-9]/.test(pw) },
    { label: "Special character (!@#$%)", ok: /[!@#$%^&*(),.?":{}|<>]/.test(pw) },
  ];
  const score = rules.filter(r => r.ok).length;
  return { rules, score, strong: score === 5 };
}

// Session 30: registration now runs in the database (register_company).
// The browser never writes companies, license_keys, team_members or the
// owner's user_settings rows, and never chooses the plan or trial dates.
function registerErrorMessage(err) {
  var m = String((err && err.message) || "");
  if (m.indexOf("already_registered") !== -1) return "This account is already fully set up. Please sign in instead.";
  if (m.indexOf("already_member") !== -1) return "This email already belongs to a team. Please sign in instead.";
  if (m.indexOf("invalid_key") !== -1) return "Invalid license key. Please check and try again.";
  if (m.indexOf("key_used") !== -1) return "This license key has already been used.";
  if (m.indexOf("key_suspended") !== -1) return "This license key has been suspended. Contact support.";
  if (m.indexOf("company_required") !== -1) return "Please enter your company name.";
  return "Account setup failed. Please try again." + (m ? " (" + m + ")" : "");
}

const IS = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid " + C.inputBorder,
  fontSize: 15,
  boxSizing: "border-box",
  marginBottom: 12,
  color: C.text,
  background: C.inputBg,
  fontFamily: "inherit",
};

const BTN = {
  width: "100%",
  minHeight: 46,
  padding: "0 16px",
  borderRadius: 10,
  border: "none",
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
  marginBottom: 10,
  fontFamily: "inherit",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  boxSizing: "border-box",
};
const BTN_P = { ...BTN, background: C.teal, color: "white" };
const BTN_S = { ...BTN, background: C.card, color: C.accent, border: "1.5px solid " + C.accent };
const BTN_T = { ...BTN, background: "transparent", color: C.sub, minHeight: 44, fontSize: 14 };
const LBL = { display: "block", fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6, textAlign: "left" };
const H = { textAlign: "center", margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: C.text };
const SUB = { textAlign: "center", color: C.muted, fontSize: 14, margin: "0 0 22px", lineHeight: 1.45 };

// Line icons, same style as the app
var L_ICONS = {
  arrowLeft: ["M19 12H5", "M12 19l-7-7 7-7"],
  check: ["M20 6L9 17l-5-5"],
  alert: [{ c: [12, 12, 9] }, "M12 8v4", "M12 16h.01"],
  lock: ["M5 11h14v10H5z", "M8 11V7a4 4 0 0 1 8 0v4"],
  eye: ["M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z", { c: [12, 12, 3] }],
  eyeOff: ["M17.94 17.94A10.1 10.1 0 0 1 12 19c-7 0-11-7-11-7a18.5 18.5 0 0 1 5.06-5.94", "M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 11 7 11 7a18.6 18.6 0 0 1-2.16 3.19", "M1 1l22 22"],
  clock: [{ c: [12, 12, 9] }, "M12 7v5l3 2"],
};
function LIcon({ name, size }) {
  var parts = L_ICONS[name] || [];
  var s = size || 18;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: "block", flexShrink: 0 }}>
      {parts.map(function (p, i) { return typeof p === "string" ? <path key={i} d={p} /> : <circle key={i} cx={p.c[0]} cy={p.c[1]} r={p.c[2]} />; })}
    </svg>
  );
}

export default function Login() {
  // Forced sign-out from another device leaves a note in localStorage
  const [signedOutElsewhere] = useState(function () {
    try { return localStorage.getItem("blindspro_signout_reason") === "other_device"; } catch (e) { return false; }
  });
  const [screen, setScreen] = useState(signedOutElsewhere ? "signin" : "landing");
  const [loading, setLoading] = useState(false);
  const trialBusyRef = useRef(false); // blocks double-tap on Start Free Trial
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regCompany, setRegCompany] = useState("");
  const [regPw, setRegPw] = useState("");
  const [regPwConfirm, setRegPwConfirm] = useState("");
  const [showRegPw, setShowRegPw] = useState(false);
  const [regCountry, setRegCountry] = useState("CA"); // PATCH oct18-tax
  const [regRegion, setRegRegion] = useState("");
  const [regCurrency, setRegCurrency] = useState("USD");

  const [licenseKey, setLicenseKey] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [pendingReg, setPendingReg] = useState(null);
  const [activatedData, setActivatedData] = useState(null);

  const pwValidation = validatePassword(regPw);
  const pwMatch = regPw && regPwConfirm && regPw === regPwConfirm;
  const pwMismatch = regPwConfirm && regPw !== regPwConfirm;

  function goTo(s) {
    setError("");
    setSuccess("");
    setScreen(s);
  }

  async function handleSignIn() {
    try { localStorage.removeItem("blindspro_signout_reason"); } catch (e) {}
    if (!email.trim() || !password) { setError("Please enter your email and password."); return; }
    setLoading(true);
    setError("");
    const { data, error: err } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    if (err) {
      // PATCH oct16-signin: same message for wrong password and no account (Supabase does not say which)
      var _m = String(err.message || "").toLowerCase();
      setError(_m.indexOf("invalid login credentials") !== -1
        ? "Email or password is incorrect. If you don't have an account yet, tap Back and choose Create New Account."
        : err.message);
      setLoading(false);
      return;
    }

    let { data: userIdRow } = await supabase
      .from("user_settings")
      .select("company_id")
      .eq("key", "user_id")
      .eq("value", data.session.user.id)
      .maybeSingle();
    // Fallbacks (same as App.js): owners via companies.owner_email, staff via
    // team_members. Without these, accounts with no user_id row never got an
    // active_session row, so single-device sign-out never worked for them.
    if (!userIdRow?.company_id) {
      const { data: co } = await supabase.from("companies").select("company_id")
        .ilike("owner_email", likeExact(email)).maybeSingle();
      if (co?.company_id) userIdRow = { company_id: co.company_id };
    }
    if (!userIdRow?.company_id) {
      const { data: tm } = await supabase.from("team_members").select("company_id")
        .ilike("email", likeExact(email)).maybeSingle();
      if (tm?.company_id) userIdRow = { company_id: tm.company_id };
    }

    if (userIdRow?.company_id) {
      localStorage.setItem("blindspro_company_id", userIdRow.company_id);
      const { data: company } = await supabase
        .from("companies")
        .select("business_name")
        .eq("company_id", userIdRow.company_id)
        .single();
      if (company?.business_name) {
        localStorage.setItem("blindspro_business_name", company.business_name);
      }
    }
    // Single device enforcement — store unique session token
    var sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem("blindspro_session_id", sessionId);
    var companyId = userIdRow?.company_id || "";
    // Scoped per user id, so multiple team members can be logged in at once;
    // this still enforces single-device login for each individual person.
    if (companyId) {
      await supabase.from("user_settings").upsert([{
        key: "active_session_" + data.session.user.id,
        value: sessionId,
        company_id: companyId,
      }], { onConflict: "key,company_id" });
    }
    await supabase.auth.signOut({ scope: "others" });
    setLoading(false);
  }

  async function handleRegisterContinue() {
    setError("");
    if (!regName.trim()) { setError("Please enter your full name."); return; }
    if (!regEmail.trim()) { setError("Please enter your email."); return; }
    if (!regCompany.trim()) { setError("Please enter your company name."); return; }
    if (regCountry !== "OT" && !regRegion) { setError("Please choose your " + (regCountry === "CA" ? "province or territory." : "state.")); return; } // PATCH oct18-tax
    if (!pwValidation.strong) { setError("Password does not meet all requirements."); return; }
    if (!pwMatch) { setError("Passwords do not match."); return; }
    setPendingReg({ name: regName.trim(), email: regEmail.trim(), phone: regPhone.trim(), company: regCompany.trim(), password: regPw, country: regCountry, region: regRegion, currency: regCurrency });
    goTo("activate");
  }

  async function handleStartTrial() {
    if (trialBusyRef.current) return; // ignore double-tap while running
    if (!pendingReg) { setError("Registration data lost. Please start again."); goTo("register"); return; }
    trialBusyRef.current = true;
    try {
      // No key: the database creates the 15 day Pro trial key itself.
      await handleActivateWithKey(null);
    } finally {
      trialBusyRef.current = false;
    }
  }

  async function handleActivate() {
    if (!licenseKey.trim()) { setError("Please enter your license key."); return; }
    await handleActivateWithKey(licenseKey.trim().toUpperCase());
  }

  async function handleActivateWithKey(key) {
    if (!pendingReg) { setError("Registration data lost. Please start again."); goTo("register"); return; }
    setLoading(true);
    setError("");

    // Step 1: If a key was entered, check it before creating the account.
    // check_license_key only returns a status word, never key details.
    if (key) {
      const { data: keyStatus, error: keyErr } = await supabase.rpc("check_license_key", { p_key: key });
      if (keyErr) { setError("Could not check the license key. Please try again."); setLoading(false); return; }
      if (keyStatus === "invalid") { setError("Invalid license key. Please check and try again."); setLoading(false); return; }
      if (keyStatus === "suspended") { setError("This license key has been suspended. Contact support."); setLoading(false); return; }
      if (keyStatus === "used") { setError("This license key has already been used."); setLoading(false); return; }
    }

    // Step 2: Set registering flag to block onAuthStateChange redirect
    localStorage.setItem("blindspro_registering", "true");

    // Step 3: Create auth user
    const { data: authData, error: authErr } = await supabase.auth.signUp({
      email: pendingReg.email,
      password: pendingReg.password,
      options: { data: { full_name: pendingReg.name } }
    });

    let userId = authData?.user?.id;

    // Some Supabase settings return no error and no session when the email
    // already exists (or needs confirming). Try signing in before giving up.
    if (!authErr && !authData?.session) {
      const { data: siData, error: siErr } = await supabase.auth.signInWithPassword({
        email: pendingReg.email,
        password: pendingReg.password,
      });
      if (siErr || !siData?.user?.id) {
        localStorage.removeItem("blindspro_registering");
        setError("This email may already be registered. Please sign in, or use Forgot Password. If you just signed up, check your email to confirm your account first.");
        setLoading(false);
        return;
      }
      userId = siData.user.id;
    }

    if (authErr) {
      const isAlreadyRegistered = authErr.message && authErr.message.toLowerCase().includes("already registered");

      if (isAlreadyRegistered) {
        // Try signing in with the password just entered, in case this is
        // an abandoned registration (auth user exists, setup never finished)
        const { data: signInData, error: signInErr } = await supabase.auth.signInWithPassword({
          email: pendingReg.email,
          password: pendingReg.password,
        });

        if (signInErr || !signInData?.user?.id) {
          localStorage.removeItem("blindspro_registering");
          setError("This email is already registered. Please sign in, or use Forgot Password if you don't remember your password.");
          setLoading(false);
          return;
        }

        const { data: existingCompany } = await supabase
          .from("companies")
          .select("company_id")
          .ilike("owner_email", likeExact(pendingReg.email))
          .maybeSingle();

        if (existingCompany) {
          localStorage.removeItem("blindspro_registering");
          await supabase.auth.signOut();
          setError("This account is already fully set up. Please sign in instead.");
          setLoading(false);
          return;
        }

        // No company yet - this was an abandoned registration. Resume setup.
        userId = signInData.user.id;
      } else {
        localStorage.removeItem("blindspro_registering");
        setError(authErr.message);
        setLoading(false);
        return;
      }
    }

    if (!userId) {
      localStorage.removeItem("blindspro_registering");
      setError("Account creation failed. Please try again.");
      setLoading(false);
      return;
    }

    // Step 4: Create company, owner team row, owner settings and license key
    // in one database call. Plan and trial dates are decided by the database.
    const { data: reg, error: regErr } = await supabase.rpc("register_company", {
      p_company: pendingReg.company,
      p_name: pendingReg.name,
      p_key: key || null,
    });

    if (regErr || !reg || !reg.company_id) {
      localStorage.removeItem("blindspro_registering");
      setError(registerErrorMessage(regErr));
      await supabase.auth.signOut();
      setLoading(false);
      return;
    }

    const companyId = reg.company_id;

    // PATCH oct18-tax: starting tax lines and currency for the chosen country and province or state
    try {
      var _tp = presetFor((pendingReg.country || "CA"), pendingReg.region || null);
      await supabase.rpc("set_company_tax", { p_company_id: companyId, p_country: (pendingReg.country || "CA"), p_region: pendingReg.region || null, p_lines: _tp.lines, p_mode: "added", p_currency: (pendingReg.country || "CA") === "OT" ? (pendingReg.currency || "USD") : _tp.currency });
    } catch (_) {}

    // Step 5: Seed default WhatsApp templates (shared with the in app setup screen)
    await seedDefaultTemplates(companyId, pendingReg.company);

    // Step 6: Set localStorage
    localStorage.setItem("blindspro_company_id", companyId);
    localStorage.setItem("blindspro_business_name", pendingReg.company);

    // Step 7: Clear flag and sign out
    localStorage.removeItem("blindspro_registering");
    await supabase.auth.signOut();

    setActivatedData({ company: pendingReg.company, plan: reg.plan || "starter", trial: reg.trial === true, trialExpires: reg.trial_expires_at });
    setLoading(false);
    goTo("activated");
  }

  async function handleForgotPassword() {
    if (!forgotEmail.trim()) { setError("Please enter your email address."); return; }
    setLoading(true);
    setError("");
    const { error: err } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
      redirectTo: "https://www.blindsprocrm.com/reset.html",
    });
    if (err) { setError(err.message); setLoading(false); return; }
    setSuccess("If an account exists for this email, you will receive a reset link shortly. Please check your spam folder too."); // PATCH oct16-forgot
    setLoading(false);
  }

  const containerStyle = {
    minHeight: "100vh",
    background: C.page,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    boxSizing: "border-box",
    fontFamily: "inherit",
  };

  const cardStyle = {
    background: C.card,
    borderRadius: 16,
    padding: "28px 24px 22px",
    maxWidth: 400,
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid " + (DARK ? C.border : "#D5DCDB"),
    boxShadow: DARK ? "0 12px 40px rgba(0,0,0,0.25)" : "0 4px 16px rgba(28,43,42,0.08)",
  };

  const logoStyle = {
    width: 64,
    height: 68,
    borderRadius: 14,
    background: C.teal,
    border: "3px solid #1a1a1a",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    margin: "0 auto 16px",
  };

  function Logo() {
    return (
      <div style={logoStyle}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, fontWeight: 800, color: "white" }}>B</div>
        <div style={{ background: C.orange, padding: "4px 0 8px 0", textAlign: "center", fontSize: 11, fontWeight: 800, color: "white", letterSpacing: 1 }}>PRO</div>
      </div>
    );
  }

  function Note({ kind, icon, children }) {
    var fg = kind === "error" ? C.error : kind === "success" ? C.success : C.warning;
    var bg = kind === "error" ? C.errorBg : kind === "success" ? C.successBg : C.warningBg;
    return (
      <div role={kind === "error" ? "alert" : "status"} style={{ display: "flex", gap: 8, alignItems: "flex-start", background: bg, borderRadius: 10, padding: "10px 12px", color: fg, fontSize: 14, lineHeight: 1.45, marginBottom: 14, textAlign: "left" }}>
        <span style={{ marginTop: 1 }}><LIcon name={icon} size={18} /></span>
        <span>{children}</span>
      </div>
    );
  }

  function ErrorMsg() {
    return error ? <Note kind="error" icon="alert">{error}</Note> : null;
  }

  function SuccessMsg() {
    return success ? <Note kind="success" icon="check">{success}</Note> : null;
  }

  function pwToggle(shown, onTap) {
    return (
      <button type="button" aria-label={shown ? "Hide password" : "Show password"} onClick={onTap} style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", width: 40, height: 40, background: "transparent", border: "none", cursor: "pointer", color: C.muted, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <LIcon name={shown ? "eyeOff" : "eye"} size={20} />
      </button>
    );
  }

  function backBtn(to, text) {
    return (
      <button onClick={() => goTo(to)} style={{ ...BTN_T, marginBottom: 0 }}>
        <LIcon name="arrowLeft" size={16} /> {text || "Back"}
      </button>
    );
  }

  if (screen === "landing") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <Logo />
          <h1 style={{ ...H, fontSize: 26, color: C.accent }}>BlindsPro CRM</h1>
          <p style={{ ...SUB, margin: "0 0 26px" }}>Field service management for window pros</p>
          <button onClick={() => goTo("signin")} style={BTN_P}>Sign in</button>
          <button onClick={() => goTo("register")} style={BTN_S}>Create new account</button>
          <button onClick={() => goTo("forgot")} style={{ ...BTN_T, marginBottom: 4 }}>Forgot password?</button>
          <div style={{ textAlign: "center", marginTop: 14, paddingTop: 14, borderTop: "1px solid " + C.border }}>
            <span style={{ fontSize: 13, color: C.muted }}>Need help? </span>
            <a href="mailto:support@blindsprocrm.com" style={{ fontSize: 13, color: C.accent, fontWeight: 600 }}>Contact support</a>
          </div>
        </div>
      </div>
    );
  }

  if (screen === "signin") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <Logo />
          <h2 style={H}>Welcome back</h2>
          <p style={SUB}>Sign in to your BlindsPro CRM account</p>
          {signedOutElsewhere && (
            <Note kind="warning" icon="lock">You were signed out because your account was signed in on another device.</Note>
          )}
          <ErrorMsg />
          <SuccessMsg />
          <label htmlFor="li_email" style={LBL}>Email</label>
          <input id="li_email" type="email" autoComplete="email" placeholder="name@company.com" value={email} onChange={e => setEmail(e.target.value)} style={IS} autoCapitalize="none" />
          <label htmlFor="li_pw" style={LBL}>Password</label>
          <div style={{ position: "relative", marginBottom: 14 }}>
            <input id="li_pw" type={showPw ? "text" : "password"} autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleSignIn(); }} style={{ ...IS, marginBottom: 0, paddingRight: 48 }} />
            {pwToggle(showPw, () => setShowPw(!showPw))}
          </div>
          <button onClick={handleSignIn} disabled={loading} style={{ ...BTN_P, opacity: loading ? 0.7 : 1 }}>
            {loading ? "Signing in..." : "Sign in"}
          </button>
          <button onClick={() => goTo("forgot")} style={{ ...BTN_T, color: C.accent, marginBottom: 0 }}>Forgot password?</button>
          {backBtn("landing")}
        </div>
      </div>
    );
  }

  if (screen === "register") {
    return (
      <div style={containerStyle}>
        <div style={{ ...cardStyle, maxWidth: 440 }}>
          <Logo />
          <h2 style={H}>Create account</h2>
          <p style={SUB}>Step 1 of 2: Your details</p>
          <ErrorMsg />
          <label htmlFor="rg_name" style={LBL}>Full name</label>
          <input id="rg_name" autoComplete="name" value={regName} onChange={e => setRegName(e.target.value)} style={IS} />
          <label htmlFor="rg_email" style={LBL}>Email</label>
          <input id="rg_email" type="email" autoComplete="email" placeholder="name@company.com" value={regEmail} onChange={e => setRegEmail(e.target.value)} style={IS} autoCapitalize="none" />
          <label htmlFor="rg_phone" style={LBL}>Phone (optional)</label>
          <input id="rg_phone" type="tel" autoComplete="tel" value={regPhone} onChange={e => setRegPhone(e.target.value)} style={IS} />
          <label htmlFor="rg_company" style={LBL}>Company name</label>
          <input id="rg_company" autoComplete="organization" value={regCompany} onChange={e => setRegCompany(e.target.value)} style={IS} />
          {/* PATCH oct18-tax: country and province or state set the starting tax */}
          <label htmlFor="rg_country" style={LBL}>Country</label>
          <select id="rg_country" value={regCountry} onChange={e => { setRegCountry(e.target.value); setRegRegion(""); }} style={IS}>
            {COUNTRIES.map(co => <option key={co.code} value={co.code}>{co.name}</option>)}
          </select>
          {regionsFor(regCountry).length > 0 && (
            <>
              <label htmlFor="rg_region" style={LBL}>{regCountry === "CA" ? "Province or territory" : "State"}</label>
              <select id="rg_region" value={regRegion} onChange={e => setRegRegion(e.target.value)} style={{ ...IS, color: regRegion ? C.text : C.muted }}>
                <option value="">Choose...</option>
                {regionsFor(regCountry).map(rg => <option key={rg.code} value={rg.code}>{rg.name}</option>)}
              </select>
            </>
          )}
          {regCountry === "OT" && (
            <>
              <label htmlFor="rg_currency" style={LBL}>Currency</label>
              <select id="rg_currency" value={regCurrency} onChange={e => setRegCurrency(e.target.value)} style={IS}>
                {CURRENCIES.map(cu => <option key={cu} value={cu}>{cu}</option>)}
              </select>
            </>
          )}
          <div style={{ fontSize: 13, color: C.muted, marginTop: -4, marginBottom: 14 }}>Sets the tax on your quotes. You can change it later in Settings.</div>
          <label htmlFor="rg_pw" style={LBL}>Create password</label>
          <div style={{ position: "relative", marginBottom: 8 }}>
            <input id="rg_pw" type={showRegPw ? "text" : "password"} autoComplete="new-password" value={regPw} onChange={e => setRegPw(e.target.value)} style={{ ...IS, marginBottom: 0, paddingRight: 48 }} />
            {pwToggle(showRegPw, () => setShowRegPw(!showRegPw))}
          </div>
          {regPw.length > 0 && (
            <div style={{ background: C.section, border: "1px solid " + C.border, borderRadius: 10, padding: "10px 12px", marginBottom: 12, textAlign: "left" }}>
              <div style={{ marginBottom: 8 }}>
                <div style={{ height: 6, borderRadius: 3, background: C.border, overflow: "hidden" }}>
                  <div style={{ height: "100%", borderRadius: 3, width: (pwValidation.score / 5 * 100) + "%", background: pwValidation.score <= 2 ? C.error : pwValidation.score <= 3 ? C.warning : C.success, transition: "width 0.3s" }} />
                </div>
              </div>
              {pwValidation.rules.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: r.ok ? C.success : C.muted, marginBottom: 2 }}>
                  {r.ok ? <LIcon name="check" size={14} /> : <span style={{ width: 14, textAlign: "center" }}>○</span>} {r.label}
                </div>
              ))}
            </div>
          )}
          <label htmlFor="rg_pw2" style={LBL}>Confirm password</label>
          <input id="rg_pw2" type="password" autoComplete="new-password" value={regPwConfirm} onChange={e => setRegPwConfirm(e.target.value)} style={{ ...IS, borderColor: pwMismatch ? C.error : pwMatch ? C.success : C.inputBorder }} />
          {pwMismatch && <div style={{ fontSize: 13, color: C.error, marginTop: -8, marginBottom: 10, textAlign: "left" }}>Passwords do not match</div>}
          {pwMatch && <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: C.success, marginTop: -8, marginBottom: 10 }}><LIcon name="check" size={14} /> Passwords match</div>}
          <button onClick={handleRegisterContinue} disabled={loading} style={BTN_P}>Continue</button>
          {backBtn("landing")}
        </div>
      </div>
    );
  }

  if (screen === "activate") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <Logo />
          <h2 style={H}>Activate account</h2>
          <p style={SUB}>Step 2 of 2: Choose how to get started</p>
          <ErrorMsg />

          {/* Free Trial Option */}
          <div style={{ background: C.tint, border: "1.5px solid " + C.accent, borderRadius: 12, padding: 16, marginBottom: 16, textAlign: "left" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>Start a free 15 day trial</div>
            <div style={{ fontSize: 14, color: C.sub, marginBottom: 12 }}>Full Pro features included. No credit card required.</div>
            <button onClick={handleStartTrial} disabled={loading} style={{ ...BTN_P, marginBottom: 0, opacity: loading ? 0.7 : 1 }}>
              {loading ? "Setting up your trial..." : "Start free trial"}
            </button>
          </div>

          <div style={{ textAlign: "center", color: C.muted, fontSize: 14, margin: "0 0 12px" }}>or enter a license key</div>

          {/* License Key Option */}
          <input
            aria-label="License key"
            placeholder="BP-XXXX-XXXX-XXXX"
            value={licenseKey}
            onChange={e => setLicenseKey(e.target.value.toUpperCase())}
            style={{ ...IS, textAlign: "center", fontSize: 16, fontWeight: 600, letterSpacing: 2 }}
            autoCapitalize="characters"
          />
          <button onClick={handleActivate} disabled={loading} style={{ ...BTN_S, opacity: loading ? 0.7 : 1 }}>
            {loading ? "Activating..." : "Activate with license key"}
          </button>

          {backBtn("register")}
        </div>
      </div>
    );
  }

  if (screen === "forgot") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <Logo />
          <h2 style={H}>Reset password</h2>
          <p style={SUB}>Enter your email and we'll send you a reset link</p>
          <ErrorMsg />
          <SuccessMsg />
          {!success && (
            <>
              <label htmlFor="fg_email" style={LBL}>Email</label>
              <input id="fg_email" type="email" autoComplete="email" placeholder="name@company.com" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} style={IS} autoCapitalize="none" />
              <button onClick={handleForgotPassword} disabled={loading} style={{ ...BTN_P, opacity: loading ? 0.7 : 1 }}>
                {loading ? "Sending..." : "Send reset email"}
              </button>
            </>
          )}
          {backBtn("landing", "Back to sign in")}
        </div>
      </div>
    );
  }

  if (screen === "activated") {
    const isTrial = activatedData?.trial;
    const planLabel = activatedData?.plan === "business" ? "Business" : activatedData?.plan === "pro" ? "Pro" : "Starter";
    const expiryDate = isTrial && activatedData?.trialExpires
      ? new Date(activatedData.trialExpires).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })
      : null;
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ width: 64, height: 64, borderRadius: 99, background: C.successBg, color: C.success, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><LIcon name="check" size={32} /></div>
            <h2 style={{ ...H, fontSize: 24, margin: "0 0 6px" }}>Account activated</h2>
            <p style={{ margin: 0, fontSize: 14, color: C.muted }}>Your BlindsPro CRM account is ready</p>
          </div>
          <div style={{ background: C.section, border: "1px solid " + C.border, borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>Company</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 12 }}>{activatedData?.company}</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 6 }}>Plan</div>
            <div style={{ display: "inline-block", background: C.teal, color: "white", padding: "4px 14px", borderRadius: 99, fontSize: 14, fontWeight: 600 }}>{planLabel} {isTrial ? "Trial" : ""}</div>
            {isTrial && expiryDate && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: C.sub, marginTop: 10 }}><LIcon name="clock" size={16} /> Trial ends <strong>{expiryDate}</strong></div>
            )}
          </div>
          <div style={{ background: C.tint, borderRadius: 10, padding: "12px 14px", marginBottom: 16, fontSize: 14, color: C.sub, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600, color: C.accent, marginBottom: 4 }}>Next steps</div>
            <div>1. Tap <strong>Sign in now</strong> below</div>
            <div>2. Use the email and password you just created</div>
            <div>3. Start adding your customers</div>
          </div>
          <button onClick={() => goTo("signin")} style={{ ...BTN_P, marginBottom: 0 }}>
            Sign in now
          </button>
        </div>
      </div>
    );
  }

  return null;
}
